import React, { useState, useEffect } from 'react';
import { agents, phoneNumbers, telnyx } from '../api';
import {
    Phone, Plus, List, Activity, CheckCircle, XCircle,
    AlertTriangle, Info, Trash2, Loader2,
    Search, ShoppingCart, Globe, Hash, Tag, DollarSign, ChevronRight,
    RefreshCw,
} from 'lucide-react';
import AnimatedDropdown from './AnimatedDropdown';

// ─── helpers ────────────────────────────────────────────────────────────────

const COUNTRY_OPTIONS = [
    { value: 'US', label: '🇺🇸 United States' },
    { value: 'CA', label: '🇨🇦 Canada' },
    { value: 'GB', label: '🇬🇧 United Kingdom' },
    { value: 'AU', label: '🇦🇺 Australia' },
    { value: 'DE', label: '🇩🇪 Germany' },
    { value: 'FR', label: '🇫🇷 France' },
    { value: 'NL', label: '🇳🇱 Netherlands' },
    { value: 'SE', label: '🇸🇪 Sweden' },
    { value: 'NO', label: '🇳🇴 Norway' },
    { value: 'DK', label: '🇩🇰 Denmark' },
    { value: 'CH', label: '🇨🇭 Switzerland' },
    { value: 'AT', label: '🇦🇹 Austria' },
    { value: 'BE', label: '🇧🇪 Belgium' },
    { value: 'IE', label: '🇮🇪 Ireland' },
    { value: 'ES', label: '🇪🇸 Spain' },
    { value: 'IT', label: '🇮🇹 Italy' },
    { value: 'PT', label: '🇵🇹 Portugal' },
    { value: 'PL', label: '🇵🇱 Poland' },
    { value: 'CZ', label: '🇨🇿 Czech Republic' },
    { value: 'MX', label: '🇲🇽 Mexico' },
    { value: 'BR', label: '🇧🇷 Brazil' },
    { value: 'IN', label: '🇮🇳 India' },
    { value: 'SG', label: '🇸🇬 Singapore' },
    { value: 'JP', label: '🇯🇵 Japan' },
    { value: 'NZ', label: '🇳🇿 New Zealand' },
    { value: 'ZA', label: '🇿🇦 South Africa' },
];

const NUMBER_TYPE_OPTIONS = [
    { value: 'local', label: 'Local' },
    { value: 'toll_free', label: 'Toll-Free' },
    { value: 'mobile', label: 'Mobile' },
];

// ─── sub-components ─────────────────────────────────────────────────────────

function TabButton({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                active
                    ? 'bg-gradient-button text-primary-foreground shadow-md'
                    : 'text-muted-foreground hover:text-foreground hover:bg-card/80 border border-transparent hover:border-border/40'
            }`}
        >
            {children}
        </button>
    );
}

function LogPanel({ logs, onClear, sidebar = false }) {
    const getIcon = (type) => {
        if (type === 'success') return <CheckCircle className="w-4 h-4" />;
        if (type === 'error') return <XCircle className="w-4 h-4" />;
        if (type === 'warning') return <AlertTriangle className="w-4 h-4" />;
        return <Info className="w-4 h-4" />;
    };
    const getColor = (type) => {
        if (type === 'success') return 'text-success';
        if (type === 'error') return 'text-destructive';
        if (type === 'warning') return 'text-yellow-500';
        return 'text-primary';
    };

    return (
        <div
            className={`rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 flex flex-col min-h-0 shadow-elegant ${
                sidebar ? 'lg:sticky lg:top-4 lg:self-start' : ''
            }`}
        >
            <div className="flex justify-between items-center mb-4 flex-shrink-0 gap-2">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                        <List className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground truncate">Live Logs</h2>
                </div>
                <button
                    onClick={onClear}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-border/60 bg-background/50 hover:bg-background hover:border-primary/20 transition-all duration-300 text-foreground flex-shrink-0"
                >
                    <Trash2 className="w-4 h-4" />
                    Clear
                </button>
            </div>
            <div
                className={`flex-1 bg-background/30 border border-border/40 rounded-lg p-4 overflow-y-auto font-mono text-sm min-h-0 ${
                    sidebar
                        ? 'min-h-[280px] max-h-[min(70vh,calc(100vh-10rem))]'
                        : 'min-h-[160px] max-h-64'
                }`}
            >
                {logs.length === 0 ? (
                    <div className="text-muted-foreground flex items-center justify-center h-full">
                        <div className="text-center">
                            <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>No logs yet.</p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {logs.map(log => (
                            <div key={log.id} className={`flex items-start gap-2 py-1.5 px-2 rounded ${getColor(log.type)}`}>
                                <span className="text-muted-foreground text-xs mt-0.5">[{log.timestamp}]</span>
                                <span className="flex-shrink-0 mt-0.5">{getIcon(log.type)}</span>
                                <span className="flex-1">{log.message}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function PhoneNumbersTable({ phoneNumbersList, removingNumberId, onRemove }) {
    if (phoneNumbersList.length === 0) return null;

    return (
        <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 flex flex-col min-h-0 shadow-elegant">
            <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                    <Phone className="w-5 h-5 text-primary-foreground" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">
                    Active Numbers
                    <span className="ml-2 text-sm font-normal text-muted-foreground">({phoneNumbersList.length})</span>
                </h2>
            </div>
            <div className="flex-1 overflow-auto min-h-0">
                <table className="min-w-full">
                    <thead className="sticky top-0 bg-background/80 backdrop-blur-sm border-b border-border/60">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Phone Number</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Agent</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Provider</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Created</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {phoneNumbersList.map((number, index) => (
                            <tr key={number.id} className={`border-b border-border/20 ${index % 2 === 0 ? 'bg-background/20' : ''} hover:bg-background/30 transition-colors`}>
                                <td className="px-4 py-3 text-sm font-mono text-foreground">{number.e164}</td>
                                <td className="px-4 py-3 text-sm text-foreground">{number.agent_name}</td>
                                <td className="px-4 py-3 text-sm">
                                    {(() => {
                                        if (number.provider === 'vonage') {
                                            return (
                                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30">
                                                    Vonage
                                                </span>
                                            );
                                        }
                                        if (number.twilio_account_sid) {
                                            return (
                                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                                                    Twilio
                                                </span>
                                            );
                                        }
                                        return (
                                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/20 text-primary border border-primary/30">
                                                Telnyx
                                            </span>
                                        );
                                    })()}
                                </td>
                                <td className="px-4 py-3 text-sm text-muted-foreground">
                                    {number.created_at ? new Date(number.created_at).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                    <button
                                        onClick={() => onRemove(number)}
                                        disabled={removingNumberId === number.id}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 hover:border-destructive/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {removingNumberId === number.id
                                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            : <Trash2 className="w-3.5 h-3.5" />
                                        }
                                        Remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─── Buy Numbers tab ─────────────────────────────────────────────────────────

function BuyNumbersTab({ agentsList, onLog, onRefresh }) {
    const [searchForm, setSearchForm] = useState({
        countryCode: 'US',
        areaCode: '',
        contains: '',
        numberType: 'local',
        limit: 20,
    });
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    // Confirm-purchase dialog state
    const [purchasing, setPurchasing] = useState(null); // phone_number string being bought
    const [selectedAgent, setSelectedAgent] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingNumber, setPendingNumber] = useState(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        setIsSearching(true);
        setSearchResults([]);
        setHasSearched(true);
        onLog('🔍 Searching available Telnyx numbers...', 'info');

        try {
            const response = await telnyx.searchAvailableNumbers({
                countryCode: searchForm.countryCode,
                areaCode: searchForm.areaCode || undefined,
                contains: searchForm.contains || undefined,
                numberType: searchForm.numberType,
                limit: searchForm.limit,
            });
            const nums = response.data?.numbers || [];
            setSearchResults(nums);
            if (nums.length > 0) {
                onLog(`✅ Found ${nums.length} available numbers`, 'success');
            } else {
                onLog('⚠️ No numbers found for those filters. Try broadening your search.', 'warning');
            }
        } catch (error) {
            const msg = error.response?.data?.detail || error.message || 'Search failed';
            onLog(`❌ Search failed: ${msg}`, 'error');
        } finally {
            setIsSearching(false);
        }
    };

    const openConfirm = (number) => {
        setPendingNumber(number);
        setSelectedAgent('');
        setShowConfirm(true);
    };

    const handleBuyAndSetup = async () => {
        if (!selectedAgent) {
            onLog('❌ Please select an agent first', 'error');
            return;
        }

        setShowConfirm(false);
        setPurchasing(pendingNumber.phone_number);
        onLog(`🛒 Purchasing ${pendingNumber.phone_number}...`, 'info');

        try {
            const response = await telnyx.buyAndSetup(selectedAgent, pendingNumber.phone_number);
            const data = response.data;
            onLog(`✅ Number purchased and set up successfully!`, 'success');
            onLog(`📞 Number: ${data.data?.phone_number}`, 'success');
            onLog(`🎯 Agent: ${data.data?.agent_name}`, 'success');
            onLog(`📦 Order ID: ${data.order_id}`, 'info');

            // Remove purchased number from results
            setSearchResults(prev => prev.filter(n => n.phone_number !== pendingNumber.phone_number));
            onRefresh();
        } catch (error) {
            const msg = error.response?.data?.detail || error.message || 'Unknown error';
            onLog(`❌ Purchase failed: ${msg}`, 'error');
        } finally {
            setPurchasing(null);
            setPendingNumber(null);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            {/* Search Filters */}
            <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
                <div className="flex items-center gap-3 mb-5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                        <Search className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-foreground">Search Available Numbers</h2>
                        <p className="text-sm text-muted-foreground">Find and purchase Telnyx phone numbers directly from your dashboard</p>
                    </div>
                </div>

                <form onSubmit={handleSearch}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                        {/* Country */}
                        <div>
                            <label className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-2">
                                <Globe className="w-3.5 h-3.5" /> Country
                            </label>
                            <AnimatedDropdown
                                value={searchForm.countryCode}
                                onChange={(v) => setSearchForm(p => ({ ...p, countryCode: v }))}
                                options={COUNTRY_OPTIONS}
                                placeholder="Select country..."
                            />
                        </div>

                        {/* Number Type */}
                        <div>
                            <label className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-2">
                                <Tag className="w-3.5 h-3.5" /> Number Type
                            </label>
                            <AnimatedDropdown
                                value={searchForm.numberType}
                                onChange={(v) => setSearchForm(p => ({ ...p, numberType: v }))}
                                options={NUMBER_TYPE_OPTIONS}
                                placeholder="Select type..."
                            />
                        </div>

                        {/* Area Code */}
                        <div>
                            <label className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-2">
                                <Hash className="w-3.5 h-3.5" /> Area Code <span className="text-muted-foreground font-normal">(optional)</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g. 415"
                                value={searchForm.areaCode}
                                onChange={(e) => setSearchForm(p => ({ ...p, areaCode: e.target.value }))}
                                disabled={searchForm.numberType === 'toll_free'}
                                className="w-full bg-background/50 border border-border/60 text-foreground rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all placeholder:text-muted-foreground/50 disabled:opacity-40 disabled:cursor-not-allowed"
                            />
                        </div>

                        {/* Contains */}
                        <div>
                            <label className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-2">
                                <Hash className="w-3.5 h-3.5" /> Contains Digits <span className="text-muted-foreground font-normal">(optional)</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g. 7777"
                                value={searchForm.contains}
                                onChange={(e) => setSearchForm(p => ({ ...p, contains: e.target.value }))}
                                className="w-full bg-background/50 border border-border/60 text-foreground rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all placeholder:text-muted-foreground/50"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            type="submit"
                            disabled={isSearching}
                            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-button text-primary-foreground rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed btn-hover-gradient"
                        >
                            {isSearching ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Searching...</>
                            ) : (
                                <><Search className="w-4 h-4" /> Search Numbers</>
                            )}
                        </button>
                        {searchResults.length > 0 && (
                            <span className="text-sm text-muted-foreground">
                                {searchResults.length} number{searchResults.length !== 1 ? 's' : ''} found
                            </span>
                        )}
                    </div>
                </form>
            </div>

            {/* Search Results */}
            {hasSearched && (
                <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                            <ShoppingCart className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <h2 className="text-xl font-semibold text-foreground">Available Numbers</h2>
                    </div>

                    {searchResults.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="font-medium">No numbers found</p>
                            <p className="text-sm mt-1">Try adjusting your search filters</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {searchResults.map((num) => (
                                <div
                                    key={num.phone_number}
                                    className="rounded-lg border border-border/50 bg-background/40 p-4 hover:border-primary/30 hover:bg-background/60 transition-all duration-200"
                                >
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div>
                                            <p className="font-mono text-base font-semibold text-foreground">{num.phone_number}</p>
                                            {num.region && (
                                                <p className="text-xs text-muted-foreground mt-0.5">{num.region}</p>
                                            )}
                                        </div>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/15 text-primary border border-primary/20 capitalize whitespace-nowrap">
                                            {num.phone_number_type}
                                        </span>
                                    </div>

                                    <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <DollarSign className="w-3 h-3" />
                                            {num.monthly_cost !== 'N/A'
                                                ? `${num.monthly_cost} ${num.currency}/mo`
                                                : 'Pricing N/A'}
                                        </span>
                                        {num.features && num.features.length > 0 && (
                                            <span className="flex flex-wrap gap-1">
                                                {num.features.slice(0, 3).map(f => (
                                                    <span key={f} className="px-1.5 py-0.5 rounded bg-border/40 text-foreground/70">{f}</span>
                                                ))}
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => openConfirm(num)}
                                        disabled={purchasing === num.phone_number}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gradient-button text-primary-foreground text-sm font-medium hover:shadow-md hover:shadow-primary/15 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed btn-hover-gradient"
                                    >
                                        {purchasing === num.phone_number ? (
                                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Setting up...</>
                                        ) : (
                                            <><ShoppingCart className="w-3.5 h-3.5" /> Buy & Assign</>
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Confirm Purchase Modal */}
            {showConfirm && pendingNumber && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card shadow-2xl p-6 animate-in fade-in zoom-in duration-200">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md flex-shrink-0">
                                <ShoppingCart className="w-5 h-5 text-primary-foreground" />
                            </div>
                            <h3 className="text-xl font-semibold text-foreground">Confirm Purchase</h3>
                        </div>

                        {/* Number Summary */}
                        <div className="rounded-lg bg-background/50 border border-border/50 p-4 mb-5">
                            <p className="font-mono text-lg font-bold text-foreground">{pendingNumber.phone_number}</p>
                            {pendingNumber.region && (
                                <p className="text-sm text-muted-foreground">{pendingNumber.region}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                                <DollarSign className="w-3.5 h-3.5" />
                                {pendingNumber.monthly_cost !== 'N/A'
                                    ? `${pendingNumber.monthly_cost} ${pendingNumber.currency}/month`
                                    : 'Pricing N/A'}
                            </div>
                        </div>

                        {/* Agent selector */}
                        <div className="mb-5">
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Assign to Agent <span className="text-destructive">*</span>
                            </label>
                            <AnimatedDropdown
                                value={selectedAgent}
                                onChange={setSelectedAgent}
                                options={[
                                    { value: '', label: agentsList.length === 0 ? 'No agents available' : 'Choose an agent...' },
                                    ...agentsList.map(a => ({
                                        value: a.id,
                                        label: `${a.name} ${a.is_active ? '(Active)' : '(Inactive)'}`,
                                    })),
                                ]}
                                placeholder="Choose an agent..."
                            />
                        </div>

                        <div className="mb-5 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-yellow-400">
                                    This will charge your Telnyx account and take ~1 minute to fully configure. Do not click twice.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirm(false)}
                                className="flex-1 px-4 py-2.5 rounded-lg border border-border/60 bg-background/50 text-foreground hover:bg-background hover:border-primary/20 transition-all duration-200 text-sm font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBuyAndSetup}
                                disabled={!selectedAgent}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-button text-primary-foreground text-sm font-medium hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed btn-hover-gradient"
                            >
                                <ChevronRight className="w-4 h-4" />
                                Confirm Purchase
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Setup Existing tab (unchanged logic) ────────────────────────────────────

function SetupExistingTab({ agentsList, setupForm, setSetupForm, onSubmit }) {
    return (
        <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-6 shadow-elegant">
            <div className="flex items-center gap-3 mb-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                    <Plus className="w-5 h-5 text-primary-foreground" />
                </div>
                <div>
                    <h2 className="text-xl font-semibold text-foreground">Setup Existing Number</h2>
                    <p className="text-sm text-muted-foreground">Already own a Telnyx number? Connect it to an agent here.</p>
                </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Select Agent</label>
                    <AnimatedDropdown
                        value={setupForm.agentId}
                        onChange={(value) => setSetupForm(prev => ({ ...prev, agentId: value }))}
                        options={[
                            { value: '', label: agentsList.length === 0 ? 'Loading agents...' : 'Choose an agent...' },
                            ...agentsList.map(agent => ({
                                value: agent.id,
                                label: `${agent.name} ${agent.is_active ? '(Active)' : '(Inactive)'}`,
                            }))
                        ]}
                        placeholder={agentsList.length === 0 ? 'Loading agents...' : 'Choose an agent...'}
                    />
                    {agentsList.length === 0 && (
                        <p className="text-sm text-destructive mt-2">No agents found. Create an agent first in the Agents tab.</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Telnyx Phone Number</label>
                    <input
                        type="text"
                        placeholder="+1234567890"
                        value={setupForm.telnyxPhoneNumber}
                        onChange={(e) => setSetupForm(prev => ({ ...prev, telnyxPhoneNumber: e.target.value }))}
                        className="w-full bg-background/50 border border-border/60 text-foreground rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all placeholder:text-muted-foreground/50"
                        required
                    />
                    <p className="text-sm text-muted-foreground mt-2">Enter an existing Telnyx phone number you own</p>
                </div>

                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-yellow-400">
                            <span className="font-semibold">Please wait for a minute after clicking this button.</span> Do not click it twice or repeatedly.
                        </p>
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={setupForm.isLoading}
                    className="w-full bg-gradient-button text-primary-foreground py-2.5 px-4 rounded-lg font-medium hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 btn-hover-gradient"
                >
                    {setupForm.isLoading ? (
                        <><Activity className="w-4 h-4 animate-spin" /> Setting up...</>
                    ) : (
                        <><Phone className="w-4 h-4" /> Setup Telnyx Number</>
                    )}
                </button>
            </form>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

const TelnyxPhoneManagement = () => {
    const [activeTab, setActiveTab] = useState('buy'); // 'buy' | 'setup'
    const [agentsList, setAgentsList] = useState([]);
    const [phoneNumbersList, setPhoneNumbersList] = useState([]);
    const [setupForm, setSetupForm] = useState({ agentId: '', telnyxPhoneNumber: '', isLoading: false });
    const [logs, setLogs] = useState([]);
    const [removingNumberId, setRemovingNumberId] = useState(null);

    useEffect(() => { loadData(); }, []);

    const addLog = (message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, { id: Date.now(), message, type, timestamp }].slice(-30));
    };

    const loadData = async () => {
        try {
            const [agentsResponse, numbersResponse] = await Promise.all([
                agents.list(),
                phoneNumbers.list(),
            ]);
            const agentsData = agentsResponse.data.agents || agentsResponse.data || [];
            const numbersData = numbersResponse.data || [];
            setAgentsList(agentsData);
            setPhoneNumbersList(numbersData);
            addLog(`✅ Loaded ${agentsData.length} agents and ${numbersData.length} phone numbers`, 'success');
        } catch (error) {
            addLog('❌ Failed to load agents and phone numbers', 'error');
        }
    };

    const handleSetupTelnyxNumber = async (e) => {
        e.preventDefault();
        if (!setupForm.agentId || !setupForm.telnyxPhoneNumber) {
            addLog('❌ Please fill in all required fields', 'error');
            return;
        }
        setSetupForm(prev => ({ ...prev, isLoading: true }));
        addLog(`🚀 Starting Telnyx setup for ${setupForm.telnyxPhoneNumber}...`, 'info');
        try {
            const response = await telnyx.setupPhoneNumber(setupForm.agentId, setupForm.telnyxPhoneNumber);
            addLog('✅ Telnyx phone number setup completed successfully!', 'success');
            addLog(`📞 Number: ${response.data.data.phone_number}`, 'success');
            addLog(`🎯 Agent: ${response.data.data.agent_name}`, 'success');
            setSetupForm({ agentId: '', telnyxPhoneNumber: '', isLoading: false });
            await loadData();
        } catch (error) {
            let errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
            addLog(`❌ Setup failed: ${errorMessage}`, 'error');
        } finally {
            setSetupForm(prev => ({ ...prev, isLoading: false }));
        }
    };

    const handleRemoveNumber = async (number) => {
        if (!window.confirm(`Remove phone number ${number.e164} from agent ${number.agent_name}?\n\nThis will delete the LiveKit SIP trunks and dispatch rules.`)) return;
        setRemovingNumberId(number.id);
        addLog(`🗑️ Removing ${number.e164}...`, 'info');
        try {
            const response = await phoneNumbers.delete(number.id);
            const cleanup = response.data?.cleanup || [];
            cleanup.forEach(msg => addLog(`  ↳ ${msg}`, 'info'));
            addLog(`✅ Removed ${number.e164} successfully`, 'success');
            await loadData();
        } catch (error) {
            addLog(`❌ Failed to remove ${number.e164}: ${error.response?.data?.detail || error.message}`, 'error');
        } finally {
            setRemovingNumberId(null);
        }
    };

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground mb-1">Phone Numbers</h1>
                <p className="text-muted-foreground">Buy new Telnyx numbers or configure existing ones — all without leaving the dashboard</p>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-2 mb-6 flex-wrap">
                <TabButton active={activeTab === 'buy'} onClick={() => setActiveTab('buy')}>
                    <ShoppingCart className="w-4 h-4" /> Buy & Assign Numbers
                </TabButton>
                <TabButton active={activeTab === 'setup'} onClick={() => setActiveTab('setup')}>
                    <Plus className="w-4 h-4" /> Setup Existing Number
                </TabButton>

                {/* Refresh */}
                <button
                    onClick={loadData}
                    className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg border border-border/60 bg-card/80 backdrop-blur-sm hover:bg-card hover:border-primary/20 transition-all duration-300 text-foreground text-sm"
                >
                    <RefreshCw className="w-4 h-4" /> Refresh
                </button>
            </div>

            {/* Tab content */}
            <div className="flex-1 flex flex-col gap-6 min-h-0">

                {/* BUY TAB — main content + logs sidebar */}
                {activeTab === 'buy' && (
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(280px,360px)] gap-6 items-start min-h-0">
                        <div className="min-w-0 flex flex-col gap-6">
                            <BuyNumbersTab
                                agentsList={agentsList}
                                onLog={addLog}
                                onRefresh={loadData}
                            />
                        </div>
                        <LogPanel
                            logs={logs}
                            onClear={() => setLogs([])}
                            sidebar
                        />
                    </div>
                )}

                {/* SETUP EXISTING TAB */}
                {activeTab === 'setup' && (
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr] gap-6">
                        <div className="flex flex-col gap-6">
                            <SetupExistingTab
                                agentsList={agentsList}
                                setupForm={setupForm}
                                setSetupForm={setSetupForm}
                                onSubmit={handleSetupTelnyxNumber}
                            />
                            <LogPanel logs={logs} onClear={() => setLogs([])} />
                        </div>
                        <div className="flex flex-col gap-6">
                            <PhoneNumbersTable
                                phoneNumbersList={phoneNumbersList}
                                removingNumberId={removingNumberId}
                                onRemove={handleRemoveNumber}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TelnyxPhoneManagement;
